import type { GraphQLErrorLike, NineNodeBase, SortMap } from './types'

// ============================================================================
// sort key utils
// ============================================================================

export function getOrderStrByName(name: string, isASC?: boolean): string {
  let str: string
  if (isASC === false) {
    str = `${name}_DESC`
  } else {
    str = `${name}_ASC`
  }
  return str
}

export function getStrBycCamelName(name: string): string {
  const upperCaseRegex = /[A-Z]/g
  let str = name.replace(upperCaseRegex, '_$&')
  str = str.toUpperCase()
  return str
}

export function getOrderStrBycCamelName(name: string, isASC?: boolean): string {
  const str = getStrBycCamelName(name)
  return getOrderStrByName(str, isASC)
}

export function removeSortKey(key: string, sorts: string[]): void {
  let sortEnum = getOrderStrByName(key, true)
  let pos = sorts.indexOf(sortEnum)
  if (pos < 0) {
    sortEnum = getOrderStrByName(key, false)
    pos = sorts.indexOf(sortEnum)
  }
  if (pos >= 0) {
    sorts.splice(pos, 1)
  }
}

// ============================================================================
// error utils
// ============================================================================

export function getFirstError(error: GraphQLErrorLike): GraphQLErrorLike {
  let err = error
  if (error.graphQLErrors != null && error.graphQLErrors.length > 0) {
    ;[err] = error.graphQLErrors
  }
  return err
}

export function checkError(
  error: Record<string, unknown>,
  errCBFunc: (type: string, error: unknown, info?: Record<string, unknown>) => void,
): void {
  const errInfo: Record<string, unknown> = {}

  console.log((error as { networkError?: unknown }).networkError)

  const networkError = (error as { networkError?: Record<string, unknown> }).networkError
  if (networkError != null) {
    const resultErrors = (
      networkError.result as { errors?: Array<{ message: string }> } | undefined
    )?.errors
    if (resultErrors != null) {
      console.log(resultErrors)
      resultErrors.some((err) => {
        try {
          const errProp = JSON.parse(err.message)
          if (errProp.code === 450 && errProp.field != null) {
            errInfo.code = errProp.code
            errInfo.field = errProp.field
          } else {
            return false
          }
        } catch {
          return false
        }
        return true
      })
    }
  }

  if (errInfo.code == null) {
    errCBFunc('noti', error)
  } else {
    errCBFunc('error', error, errInfo)
  }
}

// ============================================================================
// AST/GQL 내부 헬퍼
// ============================================================================

/** recursive check로 바꾸자. (원본 주석 유지) */
export function equalsProp(x: NineNodeBase, y: NineNodeBase): boolean {
  if (x === y) return true

  if (!(x instanceof Object) || !(y instanceof Object)) return false

  const xr = x as unknown as Record<string, unknown>
  const yr = y as unknown as Record<string, unknown>

  if (Object.keys(xr).some((key) => yr[key] === undefined) === true) return false
  if (Object.keys(yr).some((key) => xr[key] === undefined) === true) return false

  const ret = Object.keys(xr).some((key) => {
    const xv = xr[key]
    const yv = yr[key]
    if (
      xv instanceof Object &&
      yv instanceof Object &&
      equalsProp(xv as NineNodeBase, yv as NineNodeBase) === false
    ) {
      return true
    }
    return false
  })

  return ret === false
}

export function objToString(
  obj: string | number | boolean | Record<string, unknown>,
  isSORT = false,
): string {
  let str = ''

  if (isSORT === true) {
    return String(obj)
  }

  const type = typeof obj
  if (type === 'object') {
    let objStr = ''
    Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
      if (objStr.length > 0) {
        objStr += ', '
      }
      objStr += `${key}:${objToString(value as string | number | boolean | Record<string, unknown>)}`
    })
    if (objStr.length > 0) {
      objStr = ` ${objStr} `
    }
    str += `{${objStr}}`
  } else if (type === 'string') {
    str = `"${obj}"`
  } else {
    str = String(obj)
  }
  return str
}

export const INDENT_STR = '  '

// ============================================================================
// sort/filter 테이블 onChange 처리 헬퍼
// ============================================================================

interface SorterLike {
  columnKey?: string | null
  field?: string[] | null
  order?: 'ascend' | 'descend' | null
  [key: string]: unknown
}

export function processSorter(
  sortList: string[],
  sorter: SorterLike,
  clearPrev: boolean,
  sortMap: SortMap,
): void {
  const orderKey = sorter.columnKey != null ? sorter.columnKey : null
  const fieldKey = sorter.field != null ? sorter.field[sorter.field.length - 1] : null
  let ascEnum: string | null = null
  let desEnum: string | null = null
  const sortKey = orderKey || (fieldKey != null ? getStrBycCamelName(fieldKey) : '')

  if (clearPrev === true) {
    // 기존의 sort 정보를 제거.
    if (orderKey == null && fieldKey != null) {
      ascEnum = getOrderStrBycCamelName(fieldKey, true)
      desEnum = getOrderStrBycCamelName(fieldKey, false)
    } else if (orderKey != null) {
      ascEnum = getOrderStrByName(orderKey, true)
      desEnum = getOrderStrByName(orderKey, false)
    }

    if (ascEnum != null) {
      let pos = sortList.indexOf(ascEnum)
      if (pos >= 0) {
        sortList.splice(pos, 1)
      }
      if (desEnum != null) {
        pos = sortList.indexOf(desEnum)
        if (pos >= 0) {
          sortList.splice(pos, 1)
        }
      }
    }

    delete sortMap[sortKey]
  }

  if (sorter.order != null) {
    let sortEnum: string | null = null

    if (orderKey == null && fieldKey != null) {
      sortEnum = getOrderStrBycCamelName(fieldKey, sorter.order !== 'descend')
    } else if (orderKey != null) {
      sortEnum = getOrderStrByName(orderKey, sorter.order !== 'descend')
    }
    sortMap[sortKey] = sorter.order !== 'descend'

    if (sortEnum != null) {
      sortList.unshift(sortEnum)
    }
  }
}
